import { useEffect, useState } from "react";
import { Alert, Box, Button, Form, FormField, Input, Modal, SpaceBetween } from "@cloudscape-design/components";

interface DeleteStackModalProps {
  visible: boolean;
  stackName: string;
  onDismiss: () => void;
  onConfirm: () => void;
  loading?: boolean;
  error?: string | null;
}

/**
 * Confirmation modal for destroying a stack's underlying AWS infrastructure (as opposed to just
 * removing the stack record from this app). Since this triggers a real teardown of AWS
 * infrastructure, the bar for confirmation is higher than the generic DeleteConfirmModal: the
 * user must type the exact stack name before the Delete button becomes enabled, rather than a
 * single click.
 */
export default function DeleteStackModal({
  visible,
  stackName,
  onDismiss,
  onConfirm,
  loading = false,
  error,
}: DeleteStackModalProps) {
  const [typed, setTyped] = useState("");

  // Reset the typed confirmation text whenever the modal is (re)opened for a stack, so leftover
  // text from a previous open/stack doesn't silently satisfy the check for a new one.
  useEffect(() => {
    if (visible) setTyped("");
  }, [visible, stackName]);

  const matches = typed === stackName;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Delete stack"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" onClick={onConfirm} loading={loading} disabled={!matches}>Delete</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          <Alert type="warning" header="This will destroy the stack's AWS infrastructure">
            If deployment history exists for <strong>{stackName}</strong>, this submits a <code>destroy</code> request
            through the same Jenkins pipeline used for deploys and edits. Otherwise (e.g. the stack was imported or
            created outside this app) it issues a direct CloudFormation delete instead. Either way, the underlying
            CloudFormation stack and its resources will be torn down. This cannot be undone.
          </Alert>
          {error && <Alert type="error">{error}</Alert>}
          <FormField
            label="Confirm stack name"
            description={<>Type <strong>{stackName}</strong> to confirm deletion.</>}
          >
            <Input value={typed} onChange={({ detail }) => setTyped(detail.value)} placeholder={stackName} />
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
}
